import axios from 'axios'

import { showToast } from '@/components/toast/toastHelper'
import { router } from '@/routes'
import { isSessionlessAuthUrl } from '@/services/authUrls'
import logger from '@/services/logger'
import settings from '@/settings/settings.js'
import { useAuthStore } from '@/stores/Auth'
import { useLoadStore } from '@/stores/Loader'

import Http from './index'

const refreshAxios = axios.create({
  baseURL: settings.endpoint,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

export const urlNextExceptions = ['/login', '/maintenance']

// Controle de refresh para evitar múltiplas tentativas simultâneas
let isRefreshing = false
let refreshPromise = null

async function safeRedirect(to) {
  try {
    await router.isReady()
    // if (to.path === '/login' && router.currentRoute.value.query.next) {
    //   to.query.next = router.currentRoute.value.query.next
    // }
    await router.push(to)
  } catch (err) {
    logger.error('[safeRedirect] Navegação falhou:', err)
  }
}

export function generateCleanNextQuery(next) {
  const allNextValues = next.split('?next=').filter(link => {
    return !urlNextExceptions.includes(link)
  })

  next = allNextValues.length > 1 ? allNextValues.join('?next=') : allNextValues[0]

  return next
}

export function setupInterceptors() {
  const authStore = useAuthStore()
  const loadStore = useLoadStore()

  Http.interceptors.request.use(
    async config => {
      // Requisições marcadas com `localLoader` gerenciam o próprio loading
      // (ex.: via composable useAsync) e não acendem o overlay global.
      if (!config.localLoader) {
        loadStore.initRequest(config.url)
      }

      const token = localStorage.getItem('access')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        // `adminGlobal`: consulta administrativa sobre dados globais (lista de
        // executivos, status de relatório assíncrono). Com o header, o back
        // restringiria ao cliente vinculado (core/repositories/link.py). Só vale
        // para superuser; usuário comum sempre envia o vínculo.
        const skipClient = config.adminGlobal && authStore.isSuperuser
        if (authStore.linkId && !skipClient) {
          config.headers.Client = authStore.linkId
        }
      }
      return config
    },
    error => {
      if (!error.config?.localLoader) {
        loadStore.finishRequest()
      }
      return Promise.reject(error)
    },
  )

  Http.interceptors.response.use(
    response => {
      if (!response.config?.localLoader) {
        loadStore.finishRequest()
      }
      return response
    },
    async error => {
      const config = error.config || {}
      const url = config.url || ''
      const currentPath = router.currentRoute.value.fullPath

      if (!config.localLoader) {
        loadStore.finishRequest()
      }

      // Sem resposta do servidor (ex: desconectado). `skipMaintenanceRedirect` deixa a
      // tela tratar a falha (ex.: upload de planilha barrado na borda).
      if (!error.response) {
        if (!config.skipMaintenanceRedirect && router.currentRoute.value.path !== '/maintenance') {
          await safeRedirect({ path: '/maintenance', query: { next: currentPath } })
        }
        return Promise.reject(error)
      }

      const { status } = error.response

      if (status === 429) {
        error.response.data = {
          detail: 'Quantidade de tentativas excederam o limite.',
        }
        return Promise.reject(error)
      }

      if (!isSessionlessAuthUrl(url)) {
        if (status === 401 && !config.retry) {
          config.retry = true

          // Verifica se ainda tem refresh token antes de tentar
          const refreshToken = localStorage.getItem('refresh')
          if (!refreshToken) {
            return Promise.reject(error)
          }

          // Se já existe um refresh em andamento, aguarda ele
          if (isRefreshing && refreshPromise) {
            try {
              await refreshPromise
              return Http(config)
            } catch {
              return Promise.reject(error)
            }
          }

          // Inicia um novo processo de refresh
          isRefreshing = true
          refreshPromise = (async () => {
            try {
              const resp = await refreshAxios.post('/auth/refresh/', {
                refresh: refreshToken,
              })

              localStorage.setItem('access', resp.data.access)
              isRefreshing = false
              refreshPromise = null
              return resp
            } catch (refreshError) {
              isRefreshing = false
              refreshPromise = null

              localStorage.removeItem('access')
              localStorage.removeItem('refresh')
              await safeRedirect({ path: '/login', query: { next: currentPath } })
              showToast('Sua sessão expirou, faça login novamente!', {
                duration: 10000,
                variant: 'warning',
                title: 'Atenção!',
              })
              authStore.logout()
              throw refreshError
            }
          })()

          try {
            await refreshPromise
            return Http(config)
          } catch (refreshError) {
            return Promise.reject(refreshError)
          }
        }

        if (status === 403 && url === '/auth/refresh/') {
          localStorage.removeItem('access')
          localStorage.removeItem('refresh')
          await safeRedirect({ path: '/login', query: { next: currentPath } })
          showToast('Sua sessão expirou, faça login novamente!', {
            duration: 10000,
            variant: 'warning',
            title: 'Atenção!',
          })
          authStore.logout()
          return Promise.reject(error)
        }
      }

      return Promise.reject(error)
    },
  )
}

export default Http
